import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle, Car, History, LogOut, Package, Pencil, Plus, Printer } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { useApiMessages } from "../../hooks/useApiMessages";
import { IncidentStatusBadge, SeverityBadge, StatusBadge } from "./VisitorShared";
import { IncidentDialog, ITEM_CATEGORIES, VehicleFields } from "./VisitorForms";
import { fmtDateTime, fmtDuration, localInputToIso, toLocalInput, visitorApi } from "./visitorUtils";
import { printVisitLabel } from "./VisitLabel";

const Info = ({ label, value }) => (
  <div className="min-w-0">
    <p className="text-xs text-slate-500">{label}</p>
    <p className="text-sm font-medium text-slate-900 break-words">{value || "—"}</p>
  </div>
);

/** Detalle de una visita con salida, ítems, vehículo, incidentes, historial y correcciones. */
export const VisitDetailDialog = ({ visitId, open, onOpenChange, scope, onChanged }) => {
  const { t } = useTranslation();
  const { canManage } = useAuth();
  const { getErrorMessage } = useApiMessages();
  const [visit, setVisit] = useState(null);
  const [panel, setPanel] = useState(null); // checkout | item | vehicle | correct
  const [itemsOut, setItemsOut] = useState([]);
  const [notes, setNotes] = useState("");
  const [item, setItem] = useState({});
  const [vehicle, setVehicle] = useState(null);
  const [correction, setCorrection] = useState({});
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const panelRef = useRef(null);

  // Lleva a la vista el formulario abierto (salida, objeto, vehículo o corrección)
  useEffect(() => {
    if (panel) panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [panel]);

  const load = useCallback(async () => {
    if (!visitId) return;
    try {
      setVisit(await visitorApi.get(`/visits/${visitId}`));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitId]);

  useEffect(() => {
    if (open) {
      setVisit(null);
      setPanel(null);
      load();
    }
  }, [open, load]);

  const openPanel = (name) => {
    setPanel(name);
    setNotes("");
    setItemsOut((visit.items || []).filter((i) => i.direction === "in" && !i.checked_out).map((i) => i.id));
    setItem({ description: "", category: "other", serial: "", quantity: 1, direction: "in" });
    setVehicle(visit.vehicle || { plate: "" });
    setCorrection({
      reason: "",
      host_name: visit.host_name || "",
      destination: visit.destination || "",
      purpose: visit.purpose || "",
      check_in_at: visit.check_in_at ? toLocalInput(visit.check_in_at) : "",
      check_out_at: visit.check_out_at ? toLocalInput(visit.check_out_at) : "",
    });
  };

  const run = async (fn, successKey) => {
    setSaving(true);
    try {
      const updated = await fn();
      toast.success(t(successKey, { name: visit.visitor_name }));
      setPanel(null);
      await load();
      onChanged?.(updated);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const doCheckout = () =>
    run(() => visitorApi.post(`/visits/${visit.id}/check-out`, { items_out_ids: itemsOut, notes: notes || null }),
      "visitors.access.exitRegistered");

  const doAddItem = () => {
    if (!item.description?.trim()) return toast.error(t("visitors.vehicles.itemRequired"));
    run(() => visitorApi.post(`/visits/${visit.id}/items`, item), "visitors.vehicles.itemRegistered");
  };

  const doVehicle = () => {
    if (!vehicle?.plate?.trim()) return toast.error(t("visitors.access.plateRequired"));
    run(() => visitorApi.put(`/visits/${visit.id}/vehicle`, vehicle), "visitors.vehicles.vehicleSaved");
  };

  const doCorrect = () => {
    if (!correction.reason.trim()) return toast.error(t("visitors.history.correctionReasonRequired"));
    run(
      () =>
        visitorApi.put(`/visits/${visit.id}`, {
          ...correction,
          check_in_at: localInputToIso(correction.check_in_at),
          check_out_at: localInputToIso(correction.check_out_at),
        }),
      "visitors.history.corrected"
    );
  };

  const isInside = visit?.status === "inside";
  const pendingItems = (visit?.items || []).filter((i) => i.direction === "in" && !i.checked_out);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("visitors.history.detail")}</DialogTitle>
          </DialogHeader>
          {!visit ? (
            <div className="py-10 text-center text-slate-500">{t("visitors.common.loading")}</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start gap-4">
                {visit.visitor_photo ? (
                  <img src={visit.visitor_photo} alt="" className="h-20 w-20 rounded-lg object-cover border" />
                ) : (
                  <div className="h-20 w-20 rounded-lg bg-slate-100 flex items-center justify-center text-2xl font-bold text-slate-500">
                    {visit.visitor_name?.charAt(0)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-lg font-semibold">{visit.visitor_name}</h3>
                    <StatusBadge status={visit.status} />
                    {visit.visitor_is_blocked && <Badge variant="destructive">{t("visitors.common.blocked")}</Badge>}
                  </div>
                  <p className="text-sm text-slate-500">
                    {[
                      visit.visitor_id_type && t(`visitors.idTypes.${visit.visitor_id_type}`),
                      visit.visitor_id_number,
                      visit.visitor_company,
                      visit.visitor_phone,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  {isInside && !panel && (
                    <Button className="mt-3" onClick={() => openPanel("checkout")}>
                      <LogOut className="h-4 w-4 mr-2" />
                      {t("visitors.access.confirmCheckout")}
                    </Button>
                  )}
                  {visit.denial_reason && (
                    <p className="text-sm text-red-700 mt-1">
                      {t("visitors.access.denyReason")}: {visit.denial_reason}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 rounded-lg border p-4">
                <Info label={t("visitors.common.facility")} value={visit.facility_name} />
                <Info label={t("visitors.common.accessPoint")} value={visit.access_point} />
                <Info label={t("visitors.common.host")} value={[visit.host_name, visit.host_unit].filter(Boolean).join(" · ")} />
                <Info label={t("visitors.common.destination")} value={visit.destination} />
                <Info label={t("visitors.common.purpose")} value={visit.purpose} />
                <Info label={t("visitors.history.entryMethod")} value={t(`visitors.entryMethods.${visit.entry_method}`)} />
                <Info label={t("visitors.common.checkIn")} value={`${fmtDateTime(visit.check_in_at)} · ${visit.check_in_by_name || ""}`} />
                <Info
                  label={t("visitors.common.checkOut")}
                  value={visit.check_out_at ? `${fmtDateTime(visit.check_out_at)} · ${visit.check_out_by_name || ""}` : null}
                />
                <Info
                  label={t("visitors.common.duration")}
                  value={fmtDuration(isInside ? visit.minutes_inside : visit.duration_minutes)}
                />
                <Info label={t("visitors.access.idVerified")} value={visit.id_verified ? t("visitors.common.yes") : t("visitors.common.no")} />
                <Info label={t("visitors.common.shift")} value={visit.workday_date} />
                <Info label={t("visitors.history.hostNotified")} value={visit.host_notified_at ? fmtDateTime(visit.host_notified_at) : null} />
                {visit.notes && <Info label={t("visitors.common.notes")} value={visit.notes} />}
                {visit.checkout_notes && <Info label={t("visitors.access.checkoutNotes")} value={visit.checkout_notes} />}
              </div>

              <section>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Car className="h-4 w-4" />
                  {t("visitors.common.vehicle")}
                </h4>
                <p className="text-sm">
                  {visit.vehicle
                    ? [visit.vehicle.plate, visit.vehicle.make, visit.vehicle.model, visit.vehicle.color].filter(Boolean).join(" · ")
                    : "—"}
                </p>
              </section>

              <section>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4" />
                  {t("visitors.common.items")}
                </h4>
                {(visit.items || []).length === 0 ? (
                  <p className="text-sm text-slate-500">—</p>
                ) : (
                  <div className="border rounded-lg divide-y">
                    {visit.items.map((i) => (
                      <div key={i.id} className="px-3 py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                        <div>
                          <span className="font-medium">
                            {i.quantity > 1 ? `${i.quantity} × ` : ""}
                            {i.description}
                          </span>
                          <span className="text-slate-500">
                            {" "}
                            · {t(`visitors.itemCategories.${i.category || "other"}`)}
                            {i.serial ? ` · ${i.serial}` : ""}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{t(`visitors.vehicles.${i.direction}`)}</Badge>
                          {i.direction === "in" && (
                            <Badge variant={i.checked_out ? "secondary" : "outline"} className={!i.checked_out ? "border-amber-300 text-amber-700" : ""}>
                              {i.checked_out ? t("visitors.vehicles.checkedOut") : t("visitors.vehicles.pending")}
                            </Badge>
                          )}
                          <span className="text-xs text-slate-500">{fmtDateTime(i.recorded_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {(visit.incidents || []).length > 0 && (
                <section>
                  <h4 className="font-semibold flex items-center gap-2 mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    {t("visitors.nav.incidents")}
                  </h4>
                  <div className="border rounded-lg divide-y">
                    {visit.incidents.map((inc) => (
                      <div key={inc.id} className="px-3 py-2 text-sm flex flex-wrap gap-2 items-center justify-between">
                        <span>
                          {t(`visitors.incidentTypes.${inc.incident_type}`)} — {inc.description}
                        </span>
                        <span className="flex gap-2">
                          <SeverityBadge severity={inc.severity} />
                          <IncidentStatusBadge status={inc.status} />
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <h4 className="font-semibold flex items-center gap-2 mb-2">
                  <History className="h-4 w-4" />
                  {t("visitors.history.movements")}
                </h4>
                <ol className="border-l-2 border-slate-200 ml-2 space-y-2">
                  {(visit.movements || []).map((m) => (
                    <li key={m.id} className="ml-4 text-sm">
                      <span className="font-medium">{t(`visitors.movementTypes.${m.type}`)}</span>
                      <span className="text-slate-500">
                        {" "}
                        · {fmtDateTime(m.at)} · {m.user_name}
                      </span>
                      {m.details?.reason && <span className="text-slate-600"> — {m.details.reason}</span>}
                      {m.details?.description && <span className="text-slate-600"> — {m.details.description}</span>}
                    </li>
                  ))}
                </ol>
              </section>

              {(visit.corrections || []).length > 0 && (
                <section>
                  <h4 className="font-semibold mb-2">{t("visitors.history.corrections")}</h4>
                  <div className="space-y-2">
                    {visit.corrections.map((c) => (
                      <div key={c.id} className="rounded-lg bg-slate-50 border p-3 text-sm">
                        <p className="font-medium">
                          {c.reason} <span className="text-slate-500 font-normal">· {c.user_name} · {fmtDateTime(c.at)}</span>
                        </p>
                        {Object.keys(c.after).map((k) => (
                          <p key={k} className="text-slate-600">
                            {k}: <span className="line-through">{String(c.before[k] ?? "—")}</span> → {String(c.after[k] ?? "—")}
                          </p>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div ref={panelRef} />
              {panel === "checkout" && (
                <section className="rounded-lg border-2 border-primary/20 p-4 space-y-3">
                  <h4 className="font-semibold">{t("visitors.access.confirmCheckout")}</h4>
                  {pendingItems.length > 0 && (
                    <div className="space-y-2">
                      <Label>{t("visitors.access.itemsLeaving")}</Label>
                      {pendingItems.map((i) => (
                        <label key={i.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={itemsOut.includes(i.id)}
                            onCheckedChange={(c) =>
                              setItemsOut(c ? [...itemsOut, i.id] : itemsOut.filter((x) => x !== i.id))
                            }
                          />
                          {i.description} {i.serial ? `· ${i.serial}` : ""}
                        </label>
                      ))}
                      {itemsOut.length < pendingItems.length && (
                        <p className="text-xs text-amber-700">{t("visitors.access.pendingItemsWarning")}</p>
                      )}
                    </div>
                  )}
                  <Textarea rows={2} placeholder={t("visitors.access.checkoutNotes")} value={notes} onChange={(e) => setNotes(e.target.value)} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPanel(null)}>
                      {t("visitors.common.cancel")}
                    </Button>
                    <Button onClick={doCheckout} disabled={saving}>
                      <LogOut className="h-4 w-4 mr-2" />
                      {t("visitors.common.checkOut")}
                    </Button>
                  </div>
                </section>
              )}

              {panel === "item" && (
                <section className="rounded-lg border-2 border-primary/20 p-4 space-y-3">
                  <h4 className="font-semibold">{t("visitors.vehicles.registerItem")}</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <Label className="mb-1 block">{t("visitors.common.item")}</Label>
                      <Input value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} />
                    </div>
                    <div>
                      <Label className="mb-1 block">{t("visitors.vehicles.direction")}</Label>
                      <Select value={item.direction} onValueChange={(v) => setItem({ ...item, direction: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="in">{t("visitors.vehicles.in")}</SelectItem>
                          <SelectItem value="out">{t("visitors.vehicles.out")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="mb-1 block">{t("visitors.common.category")}</Label>
                      <Select value={item.category} onValueChange={(v) => setItem({ ...item, category: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ITEM_CATEGORIES.map((k) => (
                            <SelectItem key={k} value={k}>
                              {t(`visitors.itemCategories.${k}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="mb-1 block">{t("visitors.common.serial")}</Label>
                      <Input value={item.serial} onChange={(e) => setItem({ ...item, serial: e.target.value })} />
                    </div>
                    <div>
                      <Label className="mb-1 block">{t("visitors.common.quantity")}</Label>
                      <Input
                        type="number"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => setItem({ ...item, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPanel(null)}>
                      {t("visitors.common.cancel")}
                    </Button>
                    <Button onClick={doAddItem} disabled={saving}>
                      {t("visitors.common.save")}
                    </Button>
                  </div>
                </section>
              )}

              {panel === "vehicle" && (
                <section className="rounded-lg border-2 border-primary/20 p-4 space-y-3">
                  <h4 className="font-semibold">{t("visitors.common.vehicle")}</h4>
                  <VehicleFields value={vehicle} onChange={setVehicle} />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPanel(null)}>
                      {t("visitors.common.cancel")}
                    </Button>
                    <Button onClick={doVehicle} disabled={saving}>
                      {t("visitors.common.save")}
                    </Button>
                  </div>
                </section>
              )}

              {panel === "correct" && (
                <section className="rounded-lg border-2 border-amber-200 p-4 space-y-3">
                  <h4 className="font-semibold">{t("visitors.history.correct")}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {["host_name", "destination", "purpose"].map((k) => (
                      <div key={k}>
                        <Label className="mb-1 block">{t(`visitors.history.fields.${k}`)}</Label>
                        <Input value={correction[k]} onChange={(e) => setCorrection({ ...correction, [k]: e.target.value })} />
                      </div>
                    ))}
                    {visit.check_in_at && (
                      <div>
                        <Label className="mb-1 block">{t("visitors.common.checkIn")}</Label>
                        <Input
                          type="datetime-local"
                          value={correction.check_in_at}
                          onChange={(e) => setCorrection({ ...correction, check_in_at: e.target.value })}
                        />
                      </div>
                    )}
                    {visit.check_out_at && (
                      <div>
                        <Label className="mb-1 block">{t("visitors.common.checkOut")}</Label>
                        <Input
                          type="datetime-local"
                          value={correction.check_out_at}
                          onChange={(e) => setCorrection({ ...correction, check_out_at: e.target.value })}
                        />
                      </div>
                    )}
                    <div className="sm:col-span-2">
                      <Label className="mb-1 block">{t("visitors.history.correctionReason")} *</Label>
                      <Input value={correction.reason} onChange={(e) => setCorrection({ ...correction, reason: e.target.value })} />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setPanel(null)}>
                      {t("visitors.common.cancel")}
                    </Button>
                    <Button onClick={doCorrect} disabled={saving}>
                      {t("visitors.common.save")}
                    </Button>
                  </div>
                </section>
              )}
            </div>
          )}

          {visit && !panel && (
            <DialogFooter className="flex-wrap gap-2">
              {canManage() && visit.status !== "denied" && (
                <Button variant="outline" onClick={() => openPanel("correct")}>
                  <Pencil className="h-4 w-4 mr-2" />
                  {t("visitors.history.correct")}
                </Button>
              )}
              <Button variant="outline" onClick={() => setIncidentOpen(true)}>
                <AlertTriangle className="h-4 w-4 mr-2" />
                {t("visitors.incidents.newIncident")}
              </Button>
              {visit.status !== "denied" && (
                <Button variant="outline" onClick={() => printVisitLabel(visit, t)}>
                  <Printer className="h-4 w-4 mr-2" />
                  {t("visitors.label.print")}
                </Button>
              )}
              {isInside && (
                <>
                  <Button variant="outline" onClick={() => openPanel("vehicle")}>
                    <Car className="h-4 w-4 mr-2" />
                    {t("visitors.common.vehicle")}
                  </Button>
                  <Button variant="outline" onClick={() => openPanel("item")}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t("visitors.vehicles.registerItem")}
                  </Button>
                  <Button onClick={() => openPanel("checkout")}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {t("visitors.common.checkOut")}
                  </Button>
                </>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      {visit && (
        <IncidentDialog
          open={incidentOpen}
          onOpenChange={setIncidentOpen}
          scope={scope}
          visit={visit}
          onDone={() => {
            load();
            onChanged?.();
          }}
        />
      )}
    </>
  );
};
